import React, { Dispatch, SetStateAction, useState } from "react";
import { Box, Typography } from "@mui/material";

import List from "../../../components/List";
import { EmptyView } from "../../../components/OList";
import PageControl from "../../../components/PageControl";
import { getFullName } from "../../../helpers/formatters";
import { Client } from "../../../interfaces/ClientInterface";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { GlassOrder } from "../../../interfaces/GlassOrder";
import { GlassOrderStatus } from "../../../enum/GlassOrder";
import { useGlassOrders } from "../../../hooks/query/useGlassOrders";
import { GlassOrderStatusChip } from "./GlassOrderStatus";
import { GlassOrderListHeader } from "./Components/GlassOrderListHeader";
import GlassOrderInfo from "./GlassOrderInfo";
import GlassOrderCreateEdit from "./GlassOrderCreateEdit";

interface GlassOrderListProps {
  selectedRow?: Partial<GlassOrder> | null;
  setSelectedRow?: Dispatch<SetStateAction<Partial<GlassOrder> | null>>;
  page?: number;
  setPage?: Dispatch<SetStateAction<number>>;
  selectedClient?: Partial<Client> | null;
  setSelectedClient?: Dispatch<SetStateAction<Partial<Client> | null>>;
  isListForReport?: boolean;
  hideCreateButton?: boolean;
  enableCreateNew?: boolean;
  doNotHandleRowClick?: boolean;
}

export default function GlassOrderList({
  selectedRow: propSelectedRow,
  setSelectedRow: propSetSelectedRow,
  page: propPage,
  setPage: propSetPage,
  selectedClient: propSelectedClient,
  setSelectedClient: propSetSelectedClient,
  isListForReport = false,
  hideCreateButton = false,
  enableCreateNew,
  doNotHandleRowClick
}: GlassOrderListProps): React.JSX.Element {
  const pageSize = 20;

  const [internalPage, setInternalPage] = useState(0);
  const [internalSelectedRow, setInternalSelectedRow] = useState<Partial<GlassOrder> | null>(null);
  const [internalSelectedClient, setInternalSelectedClient] = useState<Partial<Client> | null>(
    null
  );
  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);

  const canCreate = enableCreateNew !== undefined ? enableCreateNew : !hideCreateButton;
  const page = propPage !== undefined ? propPage : internalPage;
  const setPage = propSetPage || setInternalPage;
  const selectedOrder = (
    propSelectedRow !== undefined ? propSelectedRow : internalSelectedRow
  ) as GlassOrder | null;
  const setSelectedOrder = propSetSelectedRow || setInternalSelectedRow;
  const selectedClient =
    propSelectedClient !== undefined ? propSelectedClient : internalSelectedClient;
  const setSelectedClient = propSetSelectedClient || setInternalSelectedClient;

  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: response, isLoading } = useGlassOrders({
    page,
    pageSize,
    clientIds: selectedClient?.id ? [selectedClient.id] : undefined,
    status: statusFilter !== "all" ? (statusFilter as GlassOrderStatus) : undefined
  });

  const orderData = response?.data;
  const ordersList = orderData?.results || [];
  const totalCount = orderData?.total || 0;

  return (
    <Box
      sx={{
        height: isListForReport ? "100%" : "calc(100% - 50px)",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <List<GlassOrder>
          automation="glassOrder"
          data={ordersList}
          isLoading={isLoading}
          rowHeight={50}
          hideCreateButton
          withoutSearch
          defaultSortColumn="id"
          defaultSortOrder={-1}
          isResponsive
          headerStyles={{ px: 0, py: 0 }}
          additionalHeaderContent={
            <GlassOrderListHeader
              enableCreateNew={canCreate}
              onCreateOrder={() => {
                setSelectedOrder(null);
                setShowCreateEditPanel(true);
              }}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              setPage={setPage}
            />
          }
          activeRow={selectedOrder && selectedOrder.id}
          onRowClick={(row) => {
            if (doNotHandleRowClick) {
              return;
            }
            const unselect = selectedOrder && Number(selectedOrder.id) === Number(row.id);
            if (unselect) {
              setSelectedOrder(null);
            } else {
              setSelectedOrder(row as GlassOrder);
            }
          }}
          columns={[
            {
              key: "clientName",
              label: "Client",
              sortable: true,
              sortFunction: (a, b) =>
                (a.client?.firstName || "").toLowerCase() >
                (b.client?.firstName || "").toLowerCase()
                  ? 1
                  : -1,
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box fontWeight={500}>{getFullName(row.client)}</Box>
                </Typography>
              )
            },
            {
              key: "orderDate",
              label: "Order Date",
              sortable: true,
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box>
                    {row.orderDate
                      ? `${convertADtoBS(new Date(row.orderDate)).formatted4} BS`
                      : "-"}
                  </Box>
                </Typography>
              )
            },
            {
              key: "deliveryDate",
              label: "Delivery Date",
              sortable: true,
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box>
                    {row.status === GlassOrderStatus.DELIVERED && row.deliveryDate
                      ? `${convertADtoBS(new Date(row.deliveryDate)).formatted4} BS`
                      : "-"}
                  </Box>
                </Typography>
              )
            },
            {
              key: "frame",
              label: "Frame",
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box>{row.frame || "-"}</Box>
                </Typography>
              )
            },
            {
              key: "lens",
              label: "Lens",
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box>{row.lens || "-"}</Box>
                </Typography>
              )
            },
            {
              key: "total",
              label: "Total",
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box>Rs. {row.total ?? 0}</Box>
                </Typography>
              )
            },
            {
              key: "advance",
              label: "Advance",
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box>Rs. {row.advance ?? 0}</Box>
                </Typography>
              )
            },
            {
              key: "balance",
              label: "Balance",
              formatter: (row) => (
                <Typography component="div" variant="body2">
                  <Box
                    color={(row.balance ?? 0) > 0 ? "error.main" : "success.main"}
                    fontWeight={600}
                  >
                    Rs. {row.balance ?? 0}
                  </Box>
                </Typography>
              )
            },
            {
              key: "status",
              label: "Status",
              sortable: false,
              formatter: (row) => <GlassOrderStatusChip status={row.status} />
            }
          ]}
        >
          <EmptyView>
            <Typography component="div" variant="body2">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "350px",
                  width: "100%"
                }}
              >
                <Typography component="div">
                  <Box textAlign="center" marginTop="16px">
                    No glass order records found
                  </Box>
                </Typography>
              </Box>
            </Typography>
          </EmptyView>
        </List>
      </Box>

      {!isListForReport && (
        <Box
          sx={{
            height: "40px",
            flexShrink: 0,
            borderTop: "1px solid lightgray",
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <Box width="200px">
            <PageControl
              page={page}
              onSetPage={(val) => {
                setPage(val);
              }}
              pageSize={pageSize}
              maximumDataCount={totalCount}
            />
          </Box>
        </Box>
      )}

      {selectedOrder && !showCreateEditPanel && (
        <GlassOrderInfo
          selectedRow={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onEditOpen={() => {
            setShowCreateEditPanel(true);
          }}
          onDeleteSuccess={() => {
            setSelectedOrder(null);
          }}
          onStatusUpdated={(updatedOrder) => {
            setSelectedOrder(updatedOrder);
          }}
        />
      )}

      {showCreateEditPanel && (
        <GlassOrderCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedOrder(null);
          }}
          glassOrder={selectedOrder}
          onCreateSuccess={() => {
            setSelectedOrder(null);
            setShowCreateEditPanel(false);
          }}
          onUpdateSuccess={() => {
            setSelectedOrder(null);
            setShowCreateEditPanel(false);
          }}
        />
      )}
    </Box>
  );
}
